import { useCallback } from 'react';
import { DefaultButton, Label, Panel, PanelType, PrimaryButton, Stack } from '@fluentui/react';
import { EntityPropertyFilter } from '../../../../../components_new/EntityPropertyFilter';
import { resourceSchema } from '../../../../../forms/resource/resourceSchema';
import { projectSchema } from '../../../../../forms/project/projectSchema';
import { contractSchema } from '../../../../../forms/contract/contractSchema';
import { useUISettings } from '../../../../../context/network/http/QueryProvider/queries/UISettings';
import { QueryGroup } from '../../../projectplanner/components/FilterPanel/helpers/createQueryGroup';
import { useControlledState } from '../../../../../hooks/useControlledState';
import { GridViewFilters } from '../../../../../api/generated/data-contracts';

export const ResourcePlannerFilterPanel = ({
    openPanel,
    setOpenPanel,
    onClickSave,
    onDismissPanel,
    initialFilterValue,
}: {
    openPanel: boolean;
    setOpenPanel: React.Dispatch<React.SetStateAction<boolean>>;
    onClickSave?: (filters: GridViewFilters) => void | Promise<void>;
    onDismissPanel?: () => void;
    initialFilterValue?: GridViewFilters | null;
}) => {
    const { data: uiSettings } = useUISettings();

    const [resourceFilter, setResourceFilter] = useControlledState<QueryGroup | null>(
        () => initialFilterValue?.resources || null,
        [initialFilterValue?.resources],
    );

	const [projectFilter, setProjectFilter] = useControlledState<QueryGroup | null>(
		() => initialFilterValue?.projects || null,
		[initialFilterValue?.projects],
	);

    const [contractFilter, setContractFilter] = useControlledState<QueryGroup | null>(
        () => initialFilterValue?.contracts || null,
        [initialFilterValue?.contracts],
    );

    const onSave = useCallback(() => {
        if (onClickSave) {
            onClickSave({
                resources: resourceFilter,
				projects: projectFilter,
                contracts: contractFilter,
            });
        }
    }, [onClickSave, resourceFilter, projectFilter, contractFilter]);

    const onCancel = useCallback(() => {
        onDismissPanel();
    }, [onDismissPanel]);

    const onRenderFooterContent = useCallback(() => {
        return (
            <Stack horizontal tokens={{ childrenGap: 10 }}>
                {onClickSave && <PrimaryButton onClick={onSave}>Apply</PrimaryButton>}
                {onDismissPanel && <DefaultButton onClick={onCancel}>{onClickSave ? 'Cancel' : 'Close'}</DefaultButton>}
            </Stack>
        );
    }, [onClickSave, onDismissPanel, onSave, onCancel]);

    return (
        <Panel
            isOpen={openPanel}
            isLightDismiss={true}
            onDismiss={() => setOpenPanel(false)}
            type={PanelType.custom}
            customWidth={'900px'}
            isBlocking={false}
            onRenderFooterContent={onRenderFooterContent}
            isFooterAtBottom
            headerText={'Filter editor'}
        >
            <Label>Resource filter</Label>
            <EntityPropertyFilter
                item={resourceFilter}
                schema={resourceSchema}
                customProperties={uiSettings.settings.resourceCustomProperties}
                onlyTheseProperties={uiSettings.settings.resourcePlannerFilterOptions}
                onChanged={setResourceFilter}
                entityType={'resource'}
            />
			<Label>Project filter</Label>
            <EntityPropertyFilter
                item={projectFilter}
                schema={projectSchema}
                customProperties={uiSettings.settings.projectCustomProperties}
                onlyTheseProperties={uiSettings.settings.resourcePlannerProjectFilterOptions}
                onChanged={setProjectFilter}
                entityType={'project'}
            />
            <Label>Contract filter</Label>
            <EntityPropertyFilter
                item={contractFilter}
                schema={contractSchema}
                customProperties={uiSettings.settings.contractCustomProperties}
                onlyTheseProperties={uiSettings.settings.resourcePlannerContractFilterOptions}
                onChanged={setContractFilter}
                entityType={'contract'}
            />
        </Panel>
    );
};
